/**
 * Application List Page
 *
 * Displays all halal certification applications for the company:
 * - Application status overview
 * - Filterable table of applications
 * - Quick actions (view, continue editing)
 * - Status-based filtering
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Table } from '../../components/common/Table';
import { Select } from '../../components/forms/Select';
import { ProgressBar } from '../../components/common/ProgressBar';
import { EmptyState } from '../../components/common/EmptyState';
import { TableSkeleton } from '../../components/common/Skeleton';
import { mockApi } from '../../services/mockApi';
import { formatDate } from '../../utils/formatting';
import { FileText, Plus, Filter } from 'lucide-react';
import { PermissionWrapper } from '../../components/auth';
import { UserPermission } from '../../types/user';
import type { Application, ApplicationStatus, ApplicationType } from '../../types/application';

export const ApplicationListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<Application[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applications, statusFilter]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getCompanyApplications('org-001'); // Mock company ID
      if (response.success && response.data) {
        setApplications(response.data);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...applications];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((app) => app.status === statusFilter);
    }

    setFilteredApplications(filtered);
  };

  const getStatusBadge = (status: ApplicationStatus) => {
    const statusMap: Record<ApplicationStatus, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      DRAFT: { variant: 'default', label: t('common:status.draft') },
      SUBMITTED: { variant: 'info', label: t('common:status.submitted') },
      UNDER_REVIEW: { variant: 'info', label: 'Under Review' },
      CONSULTANT_ASSIGNED: { variant: 'info', label: 'Consultant Assigned' },
      PRE_AUDIT_SCHEDULED: { variant: 'warning', label: 'Pre-Audit Scheduled' },
      PRE_AUDIT_COMPLETED: { variant: 'warning', label: 'Pre-Audit Completed' },
      AUDIT_SCHEDULED: { variant: 'warning', label: 'Audit Scheduled' },
      AUDIT_IN_PROGRESS: { variant: 'warning', label: 'Audit In Progress' },
      AUDIT_COMPLETED: { variant: 'warning', label: 'Audit Completed' },
      PENDING_APPROVAL: { variant: 'warning', label: 'Pending Approval' },
      APPROVED: { variant: 'success', label: t('common:status.approved') },
      REJECTED: { variant: 'error', label: t('common:status.rejected') },
      WITHDRAWN: { variant: 'default', label: 'Withdrawn' },
      CANCELLED: { variant: 'error', label: t('common:status.cancelled') },
    };
    const config = statusMap[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeLabel = (type: ApplicationType): string => {
    const typeMap: Record<ApplicationType, string> = {
      NEW_CERTIFICATION: 'New Certification',
      RENEWAL: 'Renewal',
      MODIFICATION: 'Modification',
      RE_CERTIFICATION: 'Re-Certification',
    };
    return typeMap[type];
  };

  const statusOptions = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'UNDER_REVIEW', label: 'Under Review' },
    { value: 'CONSULTANT_ASSIGNED', label: 'Consultant Assigned' },
    { value: 'PRE_AUDIT_SCHEDULED', label: 'Pre-Audit Scheduled' },
    { value: 'AUDIT_IN_PROGRESS', label: 'Audit In Progress' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  // Calculate stats
  const stats = {
    total: applications.length,
    draft: applications.filter((a) => a.status === 'DRAFT').length,
    inProgress: applications.filter((a) =>
      ['SUBMITTED', 'UNDER_REVIEW', 'CONSULTANT_ASSIGNED', 'PRE_AUDIT_SCHEDULED', 'AUDIT_IN_PROGRESS'].includes(a.status)
    ).length,
    approved: applications.filter((a) => a.status === 'APPROVED').length,
  };

  return (
    <PageLayout
      pageTitle={t('company:applications.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:applications.title') },
      ]}
      actions={
        <PermissionWrapper permission={UserPermission.CREATE_APPLICATION}>
          <Button variant="primary" onClick={() => navigate('/company/applications/new')}>
            + {t('company:applications.newApplication')}
          </Button>
        </PermissionWrapper>
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Total Applications</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{stats.total}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Draft</div>
            <div className="text-2xl font-bold text-gray-600 mt-1">{stats.draft}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">In Progress</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.inProgress}</div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-sm text-gray-600">Approved</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.approved}</div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Select
                value={statusFilter}
                options={statusOptions}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
            <div className="text-sm text-gray-600">
              Showing {filteredApplications.length} of {applications.length} applications
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Applications</h3>
        </CardHeader>
        <CardBody>
          {loading ? (
            <TableSkeleton rows={5} />
          ) : filteredApplications.length === 0 ? (
            applications.length === 0 ? (
              <EmptyState
                icon={<FileText className="w-10 h-10" />}
                title="No Applications Yet"
                description="Start your halal certification journey by creating your first application. Our streamlined process will guide you through every step."
                action={{
                  label: 'Create New Application',
                  onClick: () => navigate('/company/applications/new'),
                  icon: <Plus className="w-5 h-5 mr-2" />,
                }}
                secondaryAction={{
                  label: 'Learn More',
                  onClick: () => alert('Navigate to documentation'),
                }}
              />
            ) : (
              <EmptyState
                icon={<Filter className="w-10 h-10" />}
                title="No Matching Applications"
                description="No applications match your current filter criteria. Try adjusting your filters or view all applications."
                action={{
                  label: 'Clear Filters',
                  onClick: () => setStatusFilter('ALL'),
                }}
              />
            )
          ) : (
            <Table
              columns={[
                {
                  key: 'applicationNumber',
                  header: t('company:applications.applicationNumber'),
                  render: (app: Application) => (
                    <div>
                      <div className="font-medium text-blue-600 hover:underline cursor-pointer">
                        {app.applicationNumber}
                      </div>
                      <div className="text-xs text-gray-500">{getTypeLabel(app.type)}</div>
                    </div>
                  ),
                },
                {
                  key: 'submittedDate',
                  header: t('company:applications.submittedDate'),
                  render: (app: Application) => (
                    <div className="text-sm">
                      {app.submittedAt ? formatDate(app.submittedAt) : <span className="text-gray-400">Not submitted</span>}
                    </div>
                  ),
                },
                {
                  key: 'status',
                  header: t('company:applications.status'),
                  render: (app: Application) => getStatusBadge(app.status),
                },
                {
                  key: 'completeness',
                  header: 'Completeness',
                  render: (app: Application) => (
                    <div className="min-w-[150px]">
                      {app.completenessScore !== undefined ? (
                        <>
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{app.completenessScore}%</span>
                          </div>
                          <ProgressBar
                            value={app.completenessScore}
                            variant={app.completenessScore === 100 ? 'success' : 'default'}
                            size="sm"
                            showLabel={false}
                          />
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'assignedConsultant',
                  header: t('company:applications.assignedConsultant'),
                  render: (app: Application) => (
                    <div className="text-sm">
                      {app.assignedConsultantName || <span className="text-gray-400">Not assigned</span>}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (app: Application) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/company/applications/${app.id}`)}
                      >
                        {t('common:actions.view')}
                      </Button>
                      {app.status === 'DRAFT' && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => navigate(`/company/applications/${app.id}`)}
                        >
                          Continue
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
              data={filteredApplications}
              keyExtractor={(app) => app.id}
              onRowClick={(app) => navigate(`/company/applications/${app.id}`)}
            />
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};

export default ApplicationListPage;

